// Pure settings resolution, shared by widgets and wallpapers. No DOM, no
// imports — unit-tested via node --test and consumed by the shell under Vite.
// JSDoc types keep TS callers happy.
// KEEP IN SYNC with ./settings.d.mts, which hand-mirrors these
// signatures for the strict shell tsconfig (no allowJs).

/**
 * @typedef {{ key:string, type:string, label:string, help?:string, default?:unknown,
 *   min?:number, max?:number, step?:number, unit?:string,
 *   options?:{value:string,label:string}[], showWhen?:string }} SettingSpec
 */

/** `size` is host-owned: it writes cols/rows on the placement, never the bag. */
export const SIZE_KEY = "size";

/** Types whose stored value must satisfy the CURRENT min/max. */
const NUMERIC_TYPES = new Set(["slider", "number"]);

/**
 * A schema's range can narrow between versions, and a value stored under the
 * old range would otherwise reach the module untouched — a count above the new
 * ceiling can fail a mount outright, and the host's fallback then looks to the
 * user like their settings vanished. Non-finite junk (a string, NaN, Infinity)
 * falls back to the default rather than reaching a uniform.
 *
 * @param {SettingSpec} s
 * @param {unknown} raw
 * @returns {unknown}
 */
function clampToSpec(s, raw) {
  if (!NUMERIC_TYPES.has(s.type)) return raw;
  if (typeof raw !== "number" || !Number.isFinite(raw)) return s.default;
  let v = raw;
  if (typeof s.min === "number") v = Math.max(s.min, v);
  if (typeof s.max === "number") v = Math.min(s.max, v);
  return v;
}

/**
 * Stored value → schema default → undefined. Unknown stored keys are dropped.
 * Numeric values are clamped into the spec's current range.
 * `onWarn` is optional so the caller can surface a named warning while this
 * function stays pure.
 *
 * @param {readonly SettingSpec[] | undefined | null} specs
 * @param {Record<string, unknown> | undefined | null} stored
 * @param {(message: string) => void} [onWarn]
 * @returns {Record<string, unknown>}
 */
export function resolveSettings(specs, stored, onWarn) {
  /** @type {Record<string, unknown>} */
  const out = {};
  for (const s of specs || []) {
    if (s.key === SIZE_KEY && s.type !== "size") {
      onWarn?.(`widget setting "${SIZE_KEY}" is reserved by the host and was ignored`);
      continue;
    }
    // type:"size" is host-owned (it writes cols/rows on the placement), so it
    // never contributes a value. A hijacked `size` key already `continue`d above.
    if (s.type === "size") continue;
    // type:"button" is an ACTION, not a value: it has no default and is never
    // stored, so it must never enter the resolved bag either — the same
    // reasoning as "size" above. This is also what keeps a button out of
    // showWhen's "has a stored value" check for free: visibleSpecs only ever
    // sees `values[key]` as undefined for a button key.
    if (s.type === "button") continue;
    const raw = Object.prototype.hasOwnProperty.call(stored || {}, s.key)
      ? (stored || {})[s.key]
      : s.default;
    out[s.key] = clampToSpec(s, raw);
  }
  return out;
}

/**
 * Specs to render, after applying `showWhen`.
 *
 * @param {readonly SettingSpec[] | undefined | null} specs
 * @param {Record<string, unknown>} values
 * @returns {SettingSpec[]}
 */
export function visibleSpecs(specs, values) {
  return (specs || []).filter((s) => !s.showWhen || Boolean(values[s.showWhen]));
}
